import { T } from "./Translate";
import React, { useEffect, useLayoutEffect, useState, useCallback, useRef } from "react";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Dialog,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Button,
  List,
  ListItem,
  CircularProgress,
  Box,
  CardMedia,
  Grid,
  Select,
  InputLabel,
  MenuItem,
  FormControl,
  Autocomplete,
  Switch,
  FormControlLabel,
} from "@mui/material";
import MediaQuery from "react-responsive";
import { AddPhotoAlternate as AddPhotoAlternateIcon } from "../icons";
import { VideoLibrary as VideoLibraryIcon } from "../icons";
import { Close as CloseIcon } from "../icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLoggedIn } from "../session";
import { auth, db, getToken, uploadImage, uploadVideo } from "../firebase";
import EventDatesField from "../components/EventDatesField";
import "react-calendar/dist/Calendar.css";
import Atras from "./BackButton";
import EditSectionTabs from "./EditSectionTabs";
import useInfiniteScroll from "../hooks/useInfiniteScroll";
import {
  collection,
  onSnapshot,
  query,
  where,
  updateDoc,
  doc,
  getDoc,
  deleteDoc,
  deleteField,
} from "firebase/firestore";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from "../icons";
import BottomToolbar from "./BottomToolbar";
import { ArrowBackIos as ArrowBackIosIcon } from "../icons";
import { ArrowForwardIos as ArrowForwardIosIcon } from "../icons";
import {
  HORARIO_OPTIONS,
  geocodeAddress,
  searchAddressSuggestions,
  getHorarioLabel,
  eventDatesToRows,
  rowsToEventDates,
  getEventDates,
  formatEventDatesList,
  normalizeEventUrl,
} from "./eventsUtils";
import EventCountdown from "../components/EventCountdown";
import { brandColors, barColors } from "../theme";
import { pageContentSx } from "../pageLayout";
import { uploadButtonSx, uploadProgressSx, mediaPreviewStyle, mediaPreviewBoxSx, removeMediaButtonSx, removeMediaIconSx } from "./mediaFormStyles";

import SearchClearAdornment from "../components/SearchClearAdornment";
const pageHeaderInnerSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  width: "100%",
  maxWidth: { xs: "100%", md: "980px" },
  mx: "auto",
  px: 2,
  boxSizing: "border-box",
};

const EditEvents = () => {
  const appBarRef = useRef(null);
  const [appBarHeight, setAppBarHeight] = useState(160);

  // Mide el alto real del AppBar fijo en vez de adivinar un valor en px (ver
  // mismo criterio en editOffers.js/home.js/events.js).
  useLayoutEffect(() => {
    const node = appBarRef.current;
    if (!node) return;
    const updateHeight = () => setAppBarHeight(node.offsetHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [uid, setUid] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [videos, setVideos] = useState([]);
  const [aspectRatio, setAspectRatio] = useState("9:16");

  const handleLoadedMetadata = (e) => {
    const video = e.target;
    const ratio = video.videoWidth / video.videoHeight;
    setAspectRatio(ratio > 1 ? "16:9" : "9:16");
  };

  const handleVideoUpload = async (event) => {
    const files = event.target.files;
    if (!files) return;

    setLoading(true);
    const newVideos = [];

    try {
      for (const file of files) {
        const videoUrl = await uploadVideo(file);
        newVideos.push(videoUrl);
      }
      setVideos((prevVideos) => [...prevVideos, ...newVideos]);
    } catch (error) {
      console.error("Error al cargar videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveVideo = (index) => {
    setVideos((prevVideos) => prevVideos.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (selectedEventId) {
      const fetchEventData = async () => {
        const eventRef = doc(db, "events", selectedEventId);
        const eventSnap = await getDoc(eventRef);
        if (eventSnap.exists()) {
          const eventData = eventSnap.data();
          setTitle(eventData.title || "");
          setDescription(eventData.description || "");
          setDateRows(eventDatesToRows(eventData));
          setEventType(eventData.eventType || "");
          setDireccion(eventData.direccion || "");
          setHorario(eventData.horario || "");
          setEventUrl(eventData.url || "");
          setCupon(eventData.cupon || "");
          selectedAddressCoordsRef.current =
            eventData.latitude != null && eventData.longitude != null
              ? { latitude: eventData.latitude, longitude: eventData.longitude }
              : null;
          // Sin country guardado = visible en todos los países (ver filterOffersByCountry en homeUtils.js)
          setWorldwide(!eventData.country);
          setImages(eventData.images || []);
          setVideos(eventData.videos || []);
        }
      };
      fetchEventData();
    }
  }, [selectedEventId]);

  const handleSubmit = async () => {
    const eventDates = rowsToEventDates(dateRows);
    if (!title || !description || eventDates.length === 0) {
      alert("Por favor, completá el título, la descripción y al menos una fecha.");
      return;
    }
    if (images.length === 0 && videos.length === 0) {
      alert("Por favor, incluye al menos una imagen o un video.");
      return;
    }

    const normalizedUrl = normalizeEventUrl(eventUrl);
    if (normalizedUrl === null) {
      alert("Por favor, ingresá un enlace válido (por ejemplo, https://misitio.com) o dejalo vacío.");
      return;
    }

    if (!selectedEventId) {
      alert("Error: No se ha seleccionado ningún evento para actualizar.");
      return;
    }

    const eventsRef = doc(db, "events", selectedEventId);
    const coordinates =
      selectedAddressCoordsRef.current || (await geocodeAddress(direccion));

    const updateData = {
      title,
      description,
      // `date` = primera fecha, para las pantallas que leen una sola (ver eventsUtils).
      date: eventDates[0],
      dates: eventDates,
      direccion,
      horario,
      url: normalizedUrl,
      cupon: normalizedUrl ? cupon.trim() : "",
      latitude: coordinates?.latitude ?? null,
      longitude: coordinates?.longitude ?? null,
      images,
      videos,
      eventType,
      ...(worldwide ? { country: deleteField() } : {}),
    };

    try {
      await updateDoc(eventsRef, updateData);
      handleClose();
    } catch (error) {
      console.error("Error updating:", error);
      alert("Error al actualizar.");
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEditClick = (eventId) => {
    setSelectedEventId(eventId);
    setOpen(true);
  };

  const handleDelete = async (eventId) => {
    try {
      const eventRef = doc(db, "events", eventId);
      await deleteDoc(eventRef);
    } catch (error) {
      console.error("Error al eliminar la oferta:", error);
    }
  };

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dateRows, setDateRows] = useState([{ day: new Date(), time: "" }]);
  const [eventType, setEventType] = useState("");
  const [direccion, setDireccion] = useState("");
  const [horario, setHorario] = useState("");
  const [eventUrl, setEventUrl] = useState("");
  const [cupon, setCupon] = useState("");
  // Cuando está activo, borra el campo "country" al guardar para que
  // filterOffersByCountry (homeUtils.js) muestre el evento en cualquier país.
  const [worldwide, setWorldwide] = useState(false);

  const [addressOptions, setAddressOptions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimeout = useRef(null);
  const selectedAddressCoordsRef = useRef(null);

  const handleAddressInputChange = (event, newInputValue, reason) => {
    setDireccion(newInputValue);

    if (reason !== "input") return;

    selectedAddressCoordsRef.current = null;

    if (addressSearchTimeout.current) {
      clearTimeout(addressSearchTimeout.current);
    }

    if (newInputValue.trim().length < 3) {
      setAddressOptions([]);
      return;
    }

    addressSearchTimeout.current = setTimeout(async () => {
      setAddressLoading(true);
      const results = await searchAddressSuggestions(newInputValue);
      setAddressOptions(results);
      setAddressLoading(false);
    }, 400);
  };

  const handleAddressSelect = (event, newValue) => {
    if (newValue && typeof newValue === "object") {
      setDireccion(newValue.label);
      selectedAddressCoordsRef.current = {
        latitude: newValue.latitude,
        longitude: newValue.longitude,
      };
    }
  };

  const handleTitle = (event) => {
    setTitle(event.target.value);
  };

  const handleDescription = (event) => {
    setDescription(event.target.value);
  };

  const handleHorario = (event) => {
    setHorario(event.target.value);
  };

  const handleEventTypeChange = (event) => {
    setEventType(event.target.value);
  };


  const [images, setImages] = useState([]);

  const handleImage = async (event) => {
    const files = event.target.files;

    if (!files) {
      return;
    }

    setLoading(true);

    try {
      const newImages = [];

      for (const file of files) {
        const imageUrl = await uploadImage(file);
        newImages.push(imageUrl);
      }

      setImages((prevImages) => [...prevImages, ...newImages]);
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
  };

  const [events, setEvents] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [resultado, setResultados] = useState([]);
  // Muestra de a 10 publicaciones y suma otras 10 al hacer scroll; vuelve a
  // la primera tanda cuando cambia la búsqueda.
  const { limit: listLimit, sentinelRef: listSentinelRef } = useInfiniteScroll(busqueda, 10);

  const buscar = useCallback(() => {
    const valorBusqueda = busqueda.toLowerCase();
    const resultadosFiltrados = events.filter((event) =>
      event.title?.toLowerCase().includes(valorBusqueda)
    );
    setResultados(resultadosFiltrados);
  }, [busqueda, events]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  const formatDate = (date) => {
    if (date instanceof Date) {
      return date.toLocaleDateString();
    } else if (date && date.toDate instanceof Function) {
      return date.toDate().toLocaleDateString();
    }
    return "";
  };

  const navigate = useNavigate();

  // ?edit=<id> abre directo el editor de esa publicación (ej. desde el menú ⋮
  // del perfil). Se saca el parámetro para que no se vuelva a abrir.
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");
  useEffect(() => {
    if (!editParam || !events.some((item) => item.id === editParam)) return;
    handleEditClick(editParam);
    setSearchParams({}, { replace: true });
  }, [editParam, events]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const checkAuthAndFetchEvents = async () => {
      if (!isLoggedIn()) {
        navigate("/login");
        return;
      }

      try {
        const token = await getToken();

        if (!token) {
          console.error("No se pudo obtener el token de autenticación.");
          return;
        }

        const currentUser = auth.currentUser;
        if (!currentUser) {
          console.error("No hay un usuario autenticado.");
          return;
        }
        const { uid } = currentUser;
        setUid(uid);

        setLoading(true);
        const q = query(collection(db, "events"), where("uid", "==", uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
          const eventsData = snapshot.docs.map((doc) => {
            const data = doc.data();
            if (typeof data.name !== "string") {
              console.error("Unexpected data type for name field:", data.name);
              return { ...data, name: "" };
            }
            return {
              ...data,
              id: doc.id,
              createdAt: data.createdAt.toDate(),
              eventType: data.eventType || "",
            };
          });
          setEvents(eventsData);
          setLoading(false);
        });
        return () => unsubscribe();
      } catch (error) {
        console.error("Error al obtener ofertas:", error);
        setLoading(false);
      }
    };
    checkAuthAndFetchEvents();
  }, [navigate]);

  const [openImageModal, setOpenImageModal] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [modalImages, setModalImages] = useState([]);

  const handleOpenImageModal = (images) => {
    setModalImages(images);
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === modalImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? modalImages.length - 1 : prevIndex - 1
    );
  };

  const styles = {
    img: {
      maxWidth: "165px",
      maxHeight: "40px",
      display: "block",
      margin: "20px auto",
    },
    slogan: {
      background:
        "linear-gradient(90deg, rgba(41,210,165,1) 31%, rgba(12,82,203,1) 62%)",
      WebkitBackgroundClip: "text",
      color: "transparent",
      fontSize: "14px",
      fontWeight: "bold",
      lineHeight: "19px",
    },
    iconContainer: {
      display: "flex",
      width: "100%",
      borderTop: "1.5px solid rgba(255,255,255,0.2)",
      justifyContent: "space-around",
      color: "white",
    },
    iconContainerL: {
      display: "flex",
      width: "100%",
      borderTop: "1.5px solid white",
      justifyContent: "space-around",
    },
    backSlogan: {
      display: "block",
      width: "100vw",
      height: "19px",
      padding: "0px 0px 10px 0px",
      textAlign: "center",
    },
    listItem: {
      display: "block",
      width: "100%",
      border: "none",
      margin: "16px auto",
      padding: "clamp(14px, 4vw, 20px)",
      boxSizing: "border-box",
      // Links largos sin espacios en la descripción ensanchaban la tarjeta
      // más allá de la pantalla en celular.
      overflowWrap: "anywhere",
      backgroundColor: brandColors.backgroundPaper,
      borderRadius: "12px",
      boxShadow: "0 2px 12px rgba(0,0,0,0.3)",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.12)",
      },
    },
    textField: {
      marginBottom: "30px",
    },
    contList: {
      marginTop: "-30px",
    },
    inputS: {
      display: "block",
      width: "90vw",
      height: "30px",
      marginRight: "30px",
      padding: "3px 0px 3px 20px",
      fontSize: "14px",
      border: "1px solid grey",
      borderRadius: "30px",
    },
    name: {
      color: brandColors.accent,
      fontSize: "clamp(20px, 5.5vw, 28px)",
      fontWeight: "bold",
      marginBottom: "0px",
    },
    created: {
      color: brandColors.textSecondary,
      fontSize: "12px",
      marginTop: "12px",
      marginBottom: "16px",
    },
    media: {
      width: "190px",
      marginTop: "25px",
    },
    user: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      width: "100%",
      height: "100%",
      marginTop: "10px",
      color: "white",
    },
    contUser: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-start",
      alignItems: "center",
      marginBottom: "15px",
    },
    search: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100vw",
      height: "35px",
      border: "1.5px solid rgba(255,255,255,0.2)",
      backgroundColor: "#1a1a2e",
    },
    imageGrid: {
      marginTop: "15px",
      marginBottom: "15px",
    },
    image: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    noImage: {
      width: "100%",
      height: "100px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: brandColors.backgroundDefault,
      color: brandColors.textSecondary,
    },
    imageContainer: {
      position: "relative",
      width: "100%",
      height: "200px",
    },
    mainImage: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
    },
    additionalImagesOverlay: {
      position: "absolute",
      bottom: "10px",
      right: "10px",
      width: "60px",
      height: "60px",
      backgroundColor: "rgba(0,0,0,0.5)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      color: "white",
      cursor: "pointer",
    },
    additionalImagesCount: {
      fontSize: "18px",
      fontWeight: "bold",
    },
    mediaContainer: {
      marginBottom: "24px",
      borderRadius: "8px",
      overflow: "hidden",
    },
    contentContainer: {
      paddingLeft: { xs: "0", md: "24px" },
      flex: 1,
    },
    videoContainer: {
      position: "relative",
      width: "100%",
      marginBottom: "20px",
      borderRadius: "8px",
      overflow: "hidden",
    },
    infoSection: {
      marginTop: "16px",
      marginBottom: "16px",
    },
    title: {
      color: brandColors.textPrimary,
      fontSize: "20px",
      fontWeight: "600",
      marginBottom: "12px",
      lineHeight: 1.4,
    },
    description: {
      color: brandColors.textSecondary,
      fontSize: "15px",
      lineHeight: 1.6,
      marginBottom: "16px",
    },
    event: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontSize: "16px",
      fontWeight: "500",
      color: brandColors.accent,
      marginBottom: "12px",
      padding: "8px 12px",
      backgroundColor: brandColors.backgroundDefault,
      borderRadius: "8px",
    },
    date: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      color: brandColors.accent,
      fontSize: "16px",
      fontWeight: "500",
      marginBottom: "16px",
      padding: "8px 12px",
      backgroundColor: brandColors.backgroundDefault,
      borderRadius: "8px",
    },
    buttonContainer: {
      marginTop: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
    },
    imageModal: {
      width: "100%",
      height: "100%",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      position: "fixed",
      top: 0,
      left: 0,
      backgroundColor: "rgba(0, 0, 0, 0.8)",
      zIndex: 1000,
    },
    imageModalContent: {
      maxWidth: "90%",
      maxHeight: "90%",
      position: "relative",
      backgroundColor: "#1e1e1e",
      padding: "20px",
      borderRadius: "10px",
      boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.5)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    imageModalImage: {
      width: "100%",
      height: "auto",
      borderRadius: "10px",
      marginBottom: "20px",
    },
    imageModalNavigation: {
      position: "absolute",
      bottom: "10px",
      left: "50%",
      transform: "translateX(-50%)",
      display: "flex",
      justifyContent: "space-between",
      width: "auto",
      padding: "10px",
      backgroundColor: "rgba(0,0,0,0.7)",
      borderRadius: "5px",
    },
    imageModalDescription: {
      textAlign: "center",
      marginBottom: "20px",
    },
  };

  return (
    <Container maxWidth={false} disableGutters sx={pageContentSx}>
      <AppBar ref={appBarRef} position="fixed" color="primary">
        <Toolbar disableGutters sx={{ padding: "10px 0" }}>
          <Box sx={pageHeaderInnerSx}>
            <Atras color="inherit" />
            <Typography variant="h5">
              <T>Publicaciones</T>
            </Typography>
          </Box>
        </Toolbar>
        <EditSectionTabs />
        <Box sx={{ borderTop: `1px solid ${barColors.border}`, py: 1 }}>
          <Box sx={pageHeaderInnerSx}>
            <TextField
              id="search-events-input"
              fullWidth
              size="small"
              placeholder="Buscar por nombre de evento..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              autoComplete="off"
              InputProps={{
                endAdornment: <SearchClearAdornment value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />,
                startAdornment: <SearchIcon sx={{ mr: 1, color: barColors.textMuted }} />,
                sx: {
                  color: barColors.text,
                  backgroundColor: barColors.activeBg,
                  borderRadius: "999px",
                  "& fieldset": { border: "none" },
                },
              }}
            />
          </Box>
        </Box>
      </AppBar>
      <Container disableGutters sx={{ mt: `${appBarHeight}px`, mb: 10 }}>
        <div>
          {loading ? (
            <div
              style={{ textAlign: "center", color: brandColors.accent, marginTop: 20 }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              {busqueda.trim() !== "" ? (
                resultado.length > 0 ? (
                <List sx={{ pb: 4 }}>
                  {resultado.slice(0, listLimit).map((selectedEvent) => (
                    <ListItem key={selectedEvent.id} style={styles.listItem}>
                      <Box style={styles.contUser}>
                        <CardMedia
                          component="img"
                          image={selectedEvent.image}
                          title="Perfil"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            marginRight: 16,
                            border: `2.5px solid ${brandColors.divider}`,
                          }}
                        />
                        <Typography style={styles.name}>
                          {selectedEvent.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          flexDirection: { xs: "column", md: "row" },
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          width: "100%",
                          mt: 3,
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: "100%", md: "40%" },
                            mb: { xs: 3, md: 0 },
                          }}
                        >
                          {selectedEvent.videos &&
                          selectedEvent.videos.length > 0 ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                paddingBottom:
                                  aspectRatio === "16:9" ? "56.25%" : "177.78%",
                                backgroundColor: "#000",
                                overflow: "hidden",
                                borderRadius: "5px",
                                marginBottom: "24px",
                              }}
                            >
                              <video
                                src={selectedEvent.videos[0]}
                                controls
                                muted
                                playsInline
                                autoPlay
                                onLoadedMetadata={handleLoadedMetadata}
                                style={{
                                  position: "absolute",
                                  top: "0",
                                  left: "0",
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : selectedEvent.images &&
                            selectedEvent.images.length > 0 ? (
                            <Box style={styles.imageContainer}>
                              <CardMedia
                                component="img"
                                image={selectedEvent.images[0]}
                                alt={`Imagen principal de ${selectedEvent.name}`}
                                style={styles.mainImage}
                              />
                              {selectedEvent.images.length > 1 && (
                                <Box
                                  style={styles.additionalImagesOverlay}
                                  onClick={() =>
                                    handleOpenImageModal(selectedEvent.images)
                                  }
                                >
                                  <Typography
                                    style={styles.additionalImagesCount}
                                  >
                                    +{selectedEvent.images.length - 1}
                                  </Typography>
                                  <Typography><T>Ver más</T></Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box style={styles.noImage}>
                              <Typography><T>No hay medios disponibles</T></Typography>
                            </Box>
                          )}
                        </Box>

                        <Box
                          sx={{
                            width: { xs: "100%", md: "55%" },
                            pl: { xs: 0, md: 3 },
                          }}
                        >
                          <Typography style={styles.title}>
                            <T>Título:</T> {selectedEvent.title}
                          </Typography>

                          <Typography style={styles.description}>
                            <T>Descripción:</T> {selectedEvent.description}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={styles.event}
                          >
                            <T>Tipo de evento:</T> {selectedEvent.eventType}
                          </Typography>
                          <Typography
                            variant="body2"
                            style={styles.date}
                          >
                            {getEventDates(selectedEvent).length > 1 ? <T>Fechas:</T> : <T>Fecha:</T>}{" "}
                            {formatEventDatesList(selectedEvent)}
                          </Typography>
                          <EventCountdown event={selectedEvent} />
                          {selectedEvent.horario && (
                            <Typography variant="body2" style={styles.date}>
                              <T>Horario:</T> {getHorarioLabel(selectedEvent.horario)}
                            </Typography>
                          )}
                          {selectedEvent.direccion && (
                            <Typography variant="body2" style={styles.date}>
                              <T>Dirección:</T> {selectedEvent.direccion}
                            </Typography>
                          )}
                          <Typography variant="body2" style={styles.date}>
                            <T>Entrada:</T>{" "}
                            {selectedEvent.isPaid ? (
                              <>${selectedEvent.ticketPrice}</>
                            ) : (
                              <T>Gratuita</T>
                            )}
                          </Typography>
                          {selectedEvent.isPrivate && (
                            <Typography variant="body2" style={styles.date}>
                              <T>Evento privado</T>
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "10px" }}
                            style={styles.created}
                          >
                            <T>Fecha de publicación:</T>{" "}
                            {formatDate(selectedEvent.createdAt)}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<EditIcon />}
                              onClick={() => handleEditClick(selectedEvent.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Editar</T>
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              fullWidth
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(selectedEvent.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Eliminar</T>
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                  <Typography variant="h6"><T>No encontramos resultados</T></Typography>
                  <Typography variant="body2"><T>No hay publicaciones que coincidan con tu búsqueda.</T></Typography>
                </Box>
              )
              ) : (
                <List sx={{ pb: 4 }}>
                  {events.slice(0, listLimit).map((events) => (
                    <ListItem key={events.id} style={styles.listItem}>
                      <Box style={styles.contUser}>
                        <CardMedia
                          component="img"
                          image={events.image}
                          title="Perfil"
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            marginRight: 16,
                            border: `2.5px solid ${brandColors.divider}`,
                          }}
                        />
                        <Typography style={styles.name}>
                          {events.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          flexDirection: { xs: "column", md: "row" },
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          width: "100%",
                          mt: 3,
                        }}
                      >
                        <Box sx={{ width: { xs: "100%", md: "40%" }, mb: { xs: 3, md: 0 } }}>
                          {events.videos && events.videos.length > 0 ? (
                            <div
                              style={{
                                position: "relative",
                                width: "100%",
                                paddingBottom:
                                  aspectRatio === "16:9" ? "56.25%" : "177.78%",
                                backgroundColor: "#000",
                                overflow: "hidden",
                                borderRadius: "8px",
                              }}
                            >
                              <video
                                src={events.videos[0]}
                                controls
                                muted
                                playsInline
                                autoPlay
                                onLoadedMetadata={handleLoadedMetadata}
                                style={{
                                  position: "absolute",
                                  top: "0",
                                  left: "0",
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </div>
                          ) : events.images && events.images.length > 0 ? (
                            <Box style={styles.imageContainer}>
                              <CardMedia
                                component="img"
                                image={events.images[0]}
                                alt={`Imagen principal de ${events.name}`}
                                style={styles.mainImage}
                              />
                              {events.images.length > 1 && (
                                <Box
                                  style={styles.additionalImagesOverlay}
                                  onClick={() =>
                                    handleOpenImageModal(events.images)
                                  }
                                >
                                  <Typography style={styles.additionalImagesCount}>
                                    +{events.images.length - 1}
                                  </Typography>
                                  <Typography><T>Ver más</T></Typography>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box style={styles.noImage}>
                              <Typography><T>No hay imágenes disponibles</T></Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ width: { xs: "100%", md: "55%" }, pl: { xs: 0, md: 3 } }}>
                          <Typography style={styles.title}>
                            <T>Título:</T> {events.title}
                          </Typography>
                          <Typography style={styles.description}>
                            <T>Descripción:</T> {events.description}
                          </Typography>
                          <Typography variant="body2" style={styles.event}>
                            <T>Tipo de evento:</T> {events.eventType}
                          </Typography>
                          <Typography variant="body2" style={styles.date}>
                            {getEventDates(events).length > 1 ? <T>Fechas:</T> : <T>Fecha:</T>}{" "}
                            {formatEventDatesList(events)}
                          </Typography>
                          <EventCountdown event={events} />
                          {events.horario && (
                            <Typography variant="body2" style={styles.date}>
                              <T>Horario:</T> {getHorarioLabel(events.horario)}
                            </Typography>
                          )}
                          {events.direccion && (
                            <Typography variant="body2" style={styles.date}>
                              <T>Dirección:</T> {events.direccion}
                            </Typography>
                          )}
                          <Typography variant="body2" style={styles.date}>
                            <T>Entrada:</T>{" "}
                            {events.isPaid ? (
                              <>${events.ticketPrice}</>
                            ) : (
                              <T>Gratuita</T>
                            )}
                          </Typography>
                          {events.isPrivate && (
                            <Typography variant="body2" style={styles.date}>
                              <T>Evento privado</T>
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ fontSize: "10px" }} style={styles.created}>
                            <T>Fecha de publicación:</T>{" "}
                            {formatDate(events.createdAt)}
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1.5 }}>
                            <Button
                              variant="contained"
                              fullWidth
                              startIcon={<EditIcon />}
                              onClick={() => handleEditClick(events.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Editar</T>
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              fullWidth
                              startIcon={<DeleteIcon />}
                              onClick={() => handleDelete(events.id)}
                              sx={{ textTransform: "none" }}
                            >
                              <T>Eliminar</T>
                            </Button>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
              {(busqueda.trim() !== "" ? resultado : events).length > listLimit && (
                <Box ref={listSentinelRef} sx={{ height: 1 }} />
              )}
            </>
          )}
        </div>
      </Container>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle><T>Publicar Evento</T></DialogTitle>
        <DialogContent>
          <MediaQuery minWidth={1224}>
            <TextField
              required
              autoFocus
              margin="dense"
              id="title"
              label={<T>Nombre del evento</T>}
              type="text"
              fullWidth
              value={title}
              onChange={handleTitle}
            />
            <TextField
              required
              margin="dense"
              id="description"
              label={<T>Descripción del evento</T>}
              type="text"
              multiline
              rows={2}
              fullWidth
              value={description}
              onChange={handleDescription}
            />
            <EventDatesField value={dateRows} onChange={setDateRows} />
            <Autocomplete
              freeSolo
              fullWidth
              filterOptions={(options) => options}
              options={addressOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              getOptionKey={(option) =>
                typeof option === "string" ? option : `${option.label}-${option.latitude}-${option.longitude}`
              }
              loading={addressLoading}
              inputValue={direccion}
              onInputChange={handleAddressInputChange}
              onChange={handleAddressSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  id="direccion"
                  label={<T>Dirección del evento</T>}
                  placeholder="Empezá a escribir una dirección..."
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {addressLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              margin="dense"
              label={<T>Enlace del evento (opcional)</T>}
              placeholder="https://..."
              type="url"
              fullWidth
              value={eventUrl}
              onChange={(e) => setEventUrl(e.target.value)}
              helperText={<T>Web, venta de entradas o transmisión.</T>}
            />
            {eventUrl.trim() && (
            <TextField
              margin="dense"
              label={<T>Cupón de descuento (opcional)</T>}
              type="text"
              fullWidth
              inputProps={{ maxLength: 40 }}
              value={cupon}
              onChange={(e) => setCupon(e.target.value)}
              helperText={<T>Código de descuento para usar en la página del enlace.</T>}
            />
            )}
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="horario-label"><T>Horario del evento</T></InputLabel>
              <Select
                labelId="horario-label"
                id="horario"
                value={horario}
                label={<T>Horario del evento</T>}
                onChange={handleHorario}
              >
                {HORARIO_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <T>{option.label}</T>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography><T>Tipo de evento:</T></Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="event-type-label"><T>Tipo de evento</T></InputLabel>
              <Select
                labelId="event-type-label"
                id="event-type"
                value={eventType}
                label={<T>Tipo de evento</T>}
                onChange={handleEventTypeChange}
              >
                <MenuItem value="exposicion"><T>Exposición</T></MenuItem>
                <MenuItem value="innauguracion"><T>Inauguración</T></MenuItem>
                <MenuItem value="feriado"><T>Feriado</T></MenuItem>
                <MenuItem value="concierto"><T>Concierto</T></MenuItem>
                <MenuItem value="festival"><T>Festival</T></MenuItem>
                <MenuItem value="obraDeTeatro"><T>Obra de teatro</T></MenuItem>
                <MenuItem value="conferencia"><T>Conferencia</T></MenuItem>
                <MenuItem value="otro"><T>Otro</T></MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={worldwide}
                  onChange={(e) => setWorldwide(e.target.checked)}
                />
              }
              label={<T>Visible en todos los países (ignora tu ubicación actual)</T>}
            />

            <label
              htmlFor="upload-image-button"
              style={{ width: "100%", marginTop: "15px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <AddPhotoAlternateIcon />
                <T>Subir Imágenes</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImage}
              style={{ display: "none" }}
              id="upload-image-button"
              multiple
            />

            <label
              htmlFor="upload-video-button"
              style={{ width: "100%", marginTop: "10px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <VideoLibraryIcon />
                <T>Subir Videos</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              style={{ display: "none" }}
              id="upload-video-button"
              multiple
            />

            <Grid container sx={{ mt: 2 }}>
              {images.map((imageUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={mediaPreviewBoxSx(false)}>
                    <img
                      src={imageUrl}
                      alt={`Imagen ${index + 1}`}
                      style={mediaPreviewStyle}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveImage(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Grid container sx={{ mt: 2 }}>
              {videos.map((videoUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={{ position: "relative", width: "100%", maxWidth: 320 }}>
                    <video
                      src={videoUrl}
                      controls
                      style={{ width: "100%", borderRadius: "10px" }}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveVideo(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </MediaQuery>
          <MediaQuery maxWidth={1223}>
            <TextField
              required
              autoFocus
              margin="dense"
              id="title"
              label={<T>Nombre del evento</T>}
              type="text"
              fullWidth
              value={title}
              onChange={handleTitle}
            />
            <TextField
              required
              margin="dense"
              id="description"
              label={<T>Descripción del evento</T>}
              type="text"
              multiline
              rows={2}
              fullWidth
              value={description}
              onChange={handleDescription}
            />
            <EventDatesField value={dateRows} onChange={setDateRows} />
            <Autocomplete
              freeSolo
              fullWidth
              filterOptions={(options) => options}
              options={addressOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
              isOptionEqualToValue={(option, value) => option.label === value.label}
              getOptionKey={(option) =>
                typeof option === "string" ? option : `${option.label}-${option.latitude}-${option.longitude}`
              }
              loading={addressLoading}
              inputValue={direccion}
              onInputChange={handleAddressInputChange}
              onChange={handleAddressSelect}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  id="direccion"
                  label={<T>Dirección del evento</T>}
                  placeholder="Empezá a escribir una dirección..."
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {addressLoading ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              margin="dense"
              label={<T>Enlace del evento (opcional)</T>}
              placeholder="https://..."
              type="url"
              fullWidth
              value={eventUrl}
              onChange={(e) => setEventUrl(e.target.value)}
              helperText={<T>Web, venta de entradas o transmisión.</T>}
            />
            {eventUrl.trim() && (
            <TextField
              margin="dense"
              label={<T>Cupón de descuento (opcional)</T>}
              type="text"
              fullWidth
              inputProps={{ maxLength: 40 }}
              value={cupon}
              onChange={(e) => setCupon(e.target.value)}
              helperText={<T>Código de descuento para usar en la página del enlace.</T>}
            />
            )}
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="horario-label"><T>Horario del evento</T></InputLabel>
              <Select
                labelId="horario-label"
                id="horario"
                value={horario}
                label={<T>Horario del evento</T>}
                onChange={handleHorario}
              >
                {HORARIO_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <T>{option.label}</T>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography><T>Tipo de evento:</T></Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
              <InputLabel id="event-type-label"><T>Tipo de evento</T></InputLabel>
              <Select
                labelId="event-type-label"
                id="event-type"
                value={eventType}
                label={<T>Tipo de evento</T>}
                onChange={handleEventTypeChange}
              >
                <MenuItem value="exposicion"><T>Exposición</T></MenuItem>
                <MenuItem value="innauguracion"><T>Inauguración</T></MenuItem>
                <MenuItem value="feriado"><T>Feriado</T></MenuItem>
                <MenuItem value="concierto"><T>Concierto</T></MenuItem>
                <MenuItem value="festival"><T>Festival</T></MenuItem>
                <MenuItem value="obraDeTeatro"><T>Obra de teatro</T></MenuItem>
                <MenuItem value="conferencia"><T>Conferencia</T></MenuItem>
                <MenuItem value="otro"><T>Otro</T></MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              sx={{ mt: 1, display: "flex" }}
              control={
                <Switch
                  checked={worldwide}
                  onChange={(e) => setWorldwide(e.target.checked)}
                />
              }
              label={<T>Visible en todos los países (ignora tu ubicación actual)</T>}
            />

            <label
              htmlFor="upload-image-button"
              style={{ width: "100%", marginTop: "15px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <AddPhotoAlternateIcon />
                <T>Subir Imágenes</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImage}
              style={{ display: "none" }}
              id="upload-image-button"
              multiple
            />

            <label
              htmlFor="upload-video-button"
              style={{ width: "100%", marginTop: "10px" }}
            >
              <Button
                variant="contained"
                fullWidth
                component="div"
                sx={uploadButtonSx(false)}
                disabled={loading}
              >
                <VideoLibraryIcon />
                <T>Subir Videos</T>
                {loading && (
                  <CircularProgress size={40} thickness={4} sx={uploadProgressSx} />
                )}
              </Button>
            </label>
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              style={{ display: "none" }}
              id="upload-video-button"
              multiple
            />

            <Grid container sx={{ mt: 2 }}>
              {images.map((imageUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={mediaPreviewBoxSx(false)}>
                    <img
                      src={imageUrl}
                      alt={`Imagen ${index + 1}`}
                      style={mediaPreviewStyle}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveImage(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Grid container sx={{ mt: 2 }}>
              {videos.map((videoUrl, index) => (
                <Grid item xs={12} sm={6} md={4} key={index} margin={1}>
                  <Box sx={{ position: "relative", width: "100%", maxWidth: 320 }}>
                    <video
                      src={videoUrl}
                      controls
                      style={{ width: "100%", borderRadius: "10px" }}
                    />
                    <IconButton sx={removeMediaButtonSx} onClick={() => handleRemoveVideo(index)}>
                  <CloseIcon sx={removeMediaIconSx} />
                </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </MediaQuery>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            <T>Cancelar</T>
          </Button>
          <Button onClick={handleSubmit} color="primary">
            <T>Guardar</T>
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        aria-labelledby="image-modal-title"
        aria-describedby="image-modal-description"
        fullScreen
      >
        <DialogTitle id="image-modal-title"><T>Imágenes del evento</T></DialogTitle>
        <DialogContent>
          <DialogContentText id="image-modal-description">
            {openImageModal && (
              <Box style={styles.imageModalContent}>
                {modalImages && modalImages.length > 0 ? (
                  <CardMedia
                    component="img"
                    image={modalImages[currentImageIndex]}
                    alt={`Imagen ${currentImageIndex + 1} del alojamiento`}
                    style={styles.imageModalImage}
                  />
                ) : (
                  <Typography><T>No hay imágenes disponibles</T></Typography>
                )}
                <Box style={styles.imageModalNavigation}>
                  <IconButton onClick={handlePreviousImage}>
                    <ArrowBackIosIcon />
                  </IconButton>
                  <IconButton onClick={handleNextImage}>
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageModal}><T>Cerrar</T></Button>
        </DialogActions>
      </Dialog>
      <BottomToolbar />
    </Container>
  );
};

export default EditEvents;
